/**
 * Status API service for fetching public status data.
 */

// Remove trailing slash to avoid double-slash in URLs
const API_BASE = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');

export interface DeploymentComponent {
  name: string;
  status: 'operational' | 'degraded' | 'outage' | 'unknown';
  latency_ms?: number;
}

export interface DailyUptime {
  date: string;
  uptime: number;
  total_checks: number;
  operational_checks: number;
}

export interface Deployment {
  deployment_id: string;
  label: string;
  region: string;
  domain: string;
  environment: string;
  status: 'operational' | 'degraded' | 'outage' | 'unknown';
  commit_sha?: string;
  last_checked?: string;
  components?: DeploymentComponent[];
  active_incidents_count?: number;
  uptime_90d?: number;
  uptime_history?: DailyUptime[];
}

export interface Component {
  name: string;
  status: 'operational' | 'degraded' | 'outage' | 'unknown';
  description?: string;
}

export interface Incident {
  incident_id: string;
  title: string;
  status: 'investigating' | 'identified' | 'monitoring' | 'resolved';
  severity: 'minor' | 'major' | 'critical';
  created_at: string;
  updated_at: string;
  resolved_at?: string;
  updates: IncidentUpdate[];
  affected_components: string[];
}

export interface IncidentUpdate {
  timestamp: string;
  status: string;
  message: string;
}

export interface UptimeMetric {
  date: string;
  uptime_percentage: number;
  incidents_count: number;
}

export interface StatusData {
  overall_status: 'operational' | 'degraded' | 'outage' | 'unknown';
  deployments: Deployment[];
  components: Component[];
  active_incidents: Incident[];
  recent_incidents: Incident[];
  uptime_90d: number;
  uptime_history: UptimeMetric[];
  last_updated: string;
  checked_at?: string;
}

/**
 * Fetch the complete status data for the status page.
 */
export async function fetchStatus(): Promise<StatusData> {
  const response = await fetch(`${API_BASE}/api/status`);

  if (!response.ok) {
    throw new Error(`Failed to fetch status: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch incidents history with optional filtering.
 */
export async function fetchIncidents(params?: {
  status?: string;
  severity?: string;
  limit?: number;
}): Promise<Incident[]> {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.severity) searchParams.set('severity', params.severity);
  if (params?.limit) searchParams.set('limit', params.limit.toString());

  const url = `${API_BASE}/api/status/incidents${searchParams.toString() ? `?${searchParams}` : ''}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch incidents: ${response.status}`);
  }

  return response.json();
}

/**
 * Fetch uptime metrics for a specific period.
 */
export async function fetchUptimeMetrics(days: number = 90): Promise<UptimeMetric[]> {
  const response = await fetch(`${API_BASE}/api/status/uptime?days=${days}`);

  if (!response.ok) {
    throw new Error(`Failed to fetch uptime metrics: ${response.status}`);
  }

  return response.json();
}
